import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Chip
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { compressImage } from '../services/storageService';
import UploadIcon from '@mui/icons-material/Upload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DownloadIcon from '@mui/icons-material/Download';
import ShareIcon from '@mui/icons-material/Share';
import TuneIcon from '@mui/icons-material/Tune';
import { getAllProducts } from '../services/productService';
import API from '../api/config';

function VirtualTryOn() {
  const [activeStep, setActiveStep] = useState(0);
  const [userPhoto, setUserPhoto] = useState(null);
  const [tryOnResult, setTryOnResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fitMode, setFitMode] = useState('standard');
  const [garmentCategory, setGarmentCategory] = useState('upper');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const fileInputRef = useRef();
  const cameraInputRef = useRef();

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const products = await getAllProducts();
        setAvailableProducts(products);
      } catch (err) {
        console.error('Error loading products:', err);
      }
    };
    loadProducts();
  }, []);

  const steps = ['Carica la tua foto', 'Scegli un prodotto', 'Visualizza il risultato'];

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('Il file è troppo grande. Max 10MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = async () => {
        const raw = reader.result;
        let out = raw;
        try { out = await compressImage(raw, 1024, 0.75); } catch (err) { /* use raw */ }
        setUserPhoto(out);
        setActiveStep(1);
        setError(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCameraCapture = (event) => {
    handleFileUpload(event);
  };

  const handleProductSelect = (product) => {
    // Determina automaticamente la categoria in base al prodotto
    const productName = product.name.toLowerCase();
    if (productName.includes('pantalon') || productName.includes('jeans') || productName.includes('gonna')) {
      setGarmentCategory('lower');
    } else if (productName.includes('vestit') || productName.includes('abit')) {
      setGarmentCategory('full');
    } else {
      setGarmentCategory('upper');
    }

    processTryOn(product);
  };

  const processTryOn = async (product) => {
    setLoading(true);
    setActiveStep(2);
    setError(null);

    try {
      // Chiama l'API con Segmind Fit v2
      const tryOnData = {
        userPhotoUrl: userPhoto,
        productId: product.id,
        garmentImageUrl: product.imageUrl,
        category: garmentCategory,
        fitMode: fitMode,
        options: {
          preservePose: true,
          steps: showAdvanced ? 40 : 30,
          guidance: showAdvanced ? 8.0 : 7.5
        }
      };

      console.log('Sending try-on request:', tryOnData);

      const result = await API.tryOn(tryOnData);

      if (result.success) {
        setTryOnResult({
          originalPhoto: userPhoto,
          resultPhoto: result.resultUrl || result.resultImage,
          product: product,
          metadata: result.metadata,
          demo: result.demo,
          processedAt: result.processedAt || new Date().toISOString()
        });

        if (result.demo) {
          setError('Modalità demo - Configura Segmind API per risultati reali');
        }
      } else {
        throw new Error(result.error || 'Errore nel processing');
      }
    } catch (err) {
      setError(`Errore durante il virtual try-on: ${err.message}`);
      console.error('Try-on error:', err);

      // Fallback per demo
      setTryOnResult({
        originalPhoto: userPhoto,
        resultPhoto: userPhoto,
        product: product,
        demo: true,
        processedAt: new Date().toISOString()
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (tryOnResult?.resultPhoto) {
      const link = document.createElement('a');
      link.href = tryOnResult.resultPhoto;
      link.download = `provami-tryon-${Date.now()}.png`;
      link.click();
    }
  };

  const handleShare = async () => {
    if (navigator.share && tryOnResult?.resultPhoto) {
      try {
        await navigator.share({
          title: 'Il mio Try-On Virtuale',
          text: 'Guarda come mi sta questo capo!',
          url: window.location.href
        });
      } catch (err) {
        console.log('Condivisione annullata');
      }
    } else {
      alert('La condivisione non è supportata su questo browser');
    }
  };

  const resetProcess = () => {
    setActiveStep(0);
    setUserPhoto(null);
    // setSelectedProduct removed - was undefined
    setTryOnResult(null);
    setError(null);
  };

  // Products loaded from Firebase via useEffect

  return (
    <Box>
      <Typography variant="h4" gutterBottom align="center">
        Prova Virtuale
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Step 1: Upload foto */}
      {activeStep === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Carica una tua foto per iniziare
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Per migliori risultati, usa una foto frontale a figura intera con buona illuminazione
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 3 }}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept="image/*"
              style={{ display: 'none' }}
            />
            <input
              type="file"
              ref={cameraInputRef}
              onChange={handleCameraCapture}
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
            />

            <Button
              variant="contained"
              size="large"
              startIcon={<UploadIcon />}
              onClick={() => fileInputRef.current.click()}
            >
              Carica dal dispositivo
            </Button>

            <Button
              variant="outlined"
              size="large"
              startIcon={<PhotoCameraIcon />}
              onClick={() => cameraInputRef.current.click()}
            >
              Scatta una foto
            </Button>
          </Box>

          <Box sx={{ mt: 4 }}>
            <Typography variant="caption" color="text.secondary">
              Le tue foto sono processate in modo sicuro e non vengono salvate permanentemente
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Step 2: Selezione prodotto */}
      {activeStep === 1 && !loading && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Seleziona il prodotto da provare
          </Typography>

          {/* Opzioni di fitting */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mr: 2 }}>
                Modalità Fitting:
              </Typography>
              <ToggleButtonGroup
                value={fitMode}
                exclusive
                onChange={(e, newMode) => newMode && setFitMode(newMode)}
                size="small"
              >
                <ToggleButton value="tight">
                  Aderente
                </ToggleButton>
                <ToggleButton value="standard">
                  Standard
                </ToggleButton>
                <ToggleButton value="loose">
                  Largo
                </ToggleButton>
              </ToggleButtonGroup>

              <Button
                startIcon={<TuneIcon />}
                onClick={() => setShowAdvanced(!showAdvanced)}
                sx={{ ml: 'auto' }}
                size="small"
              >
                {showAdvanced ? 'Nascondi' : 'Mostra'} Opzioni
              </Button>
            </Box>

            {showAdvanced && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Categoria vestito (auto-rilevata):
                </Typography>
                <ToggleButtonGroup
                  value={garmentCategory}
                  exclusive
                  onChange={(e, newCat) => newCat && setGarmentCategory(newCat)}
                  size="small"
                >
                  <ToggleButton value="upper">
                    Parte superiore
                  </ToggleButton>
                  <ToggleButton value="lower">
                    Parte inferiore
                  </ToggleButton>
                  <ToggleButton value="full">
                    Completo
                  </ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  La categoria viene rilevata automaticamente dal prodotto selezionato
                </Typography>
              </Box>
            )}
          </Paper>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  La tua foto
                </Typography>
                <img
                  src={userPhoto}
                  alt="User"
                  style={{ width: '100%', height: 'auto', borderRadius: 8 }}
                />
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 8 }}>
              <Grid container spacing={2}>
                {availableProducts.map((product) => (
                  <Grid size={{ xs: 6, sm: 4, md: 3 }} key={product.id}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        transition: 'transform 0.2s',
                        '&:hover': { transform: 'scale(1.05)' }
                      }}
                      onClick={() => handleProductSelect(product)}
                    >
                      <img
                        src={product.imageUrl}
                        alt={product.name}
                        style={{ width: '100%', height: 150, objectFit: 'cover' }}
                      />
                      <CardContent>
                        <Typography variant="body2" noWrap>
                          {product.name}
                        </Typography>
                        <Typography variant="body2" color="primary">
                          €{product.price}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Loading */}
      {loading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 3 }}>
            Elaborazione in corso...
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            La nostra AI sta creando il tuo try-on virtuale
          </Typography>
        </Paper>
      )}

      {/* Step 3: Risultato */}
      {activeStep === 2 && !loading && tryOnResult && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <CheckCircleIcon color="success" sx={{ mr: 1 }} />
            <Typography variant="h6">
              Try-On completato con successo!
            </Typography>
            {tryOnResult.demo && (
              <Chip
                label="DEMO MODE"
                color="warning"
                size="small"
                sx={{ ml: 2 }}
              />
            )}
          </Box>

          {tryOnResult.demo && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Modalità Demo Attiva</strong><br />
                Per risultati reali con Segmind Fit v2, configura la chiave API nel file .env:
                <code style={{ display: 'block', marginTop: 8 }}>
                  SEGMIND_API_KEY=your_api_key_here
                </code>
              </Typography>
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle1" gutterBottom>
                Foto originale
              </Typography>
              <img
                src={tryOnResult.originalPhoto}
                alt="Original"
                style={{ width: '100%', height: 'auto', borderRadius: 8 }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle1" gutterBottom>
                Risultato Try-On
                {tryOnResult.metadata?.model && (
                  <Chip
                    label={tryOnResult.metadata.model === 'segmind-fit-v2' ? 'Segmind AI' : 'Demo'}
                    size="small"
                    sx={{ ml: 1 }}
                    color={tryOnResult.metadata.model === 'segmind-fit-v2' ? 'primary' : 'default'}
                  />
                )}
              </Typography>
              <Box sx={{ position: 'relative' }}>
                <img
                  src={tryOnResult.resultPhoto}
                  alt="Result"
                  style={{ width: '100%', height: 'auto', borderRadius: 8 }}
                />
                <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
                  <IconButton
                    sx={{ bgcolor: 'white', mr: 1 }}
                    onClick={handleDownload}
                  >
                    <DownloadIcon />
                  </IconButton>
                  <IconButton
                    sx={{ bgcolor: 'white' }}
                    onClick={handleShare}
                  >
                    <ShareIcon />
                  </IconButton>
                </Box>
              </Box>

              {tryOnResult.metadata && (
                <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="caption" display="block">
                    Modalità: {fitMode === 'tight' ? 'Aderente' : fitMode === 'loose' ? 'Largo' : 'Standard'}
                  </Typography>
                  <Typography variant="caption" display="block">
                    Categoria: {garmentCategory === 'upper' ? 'Superiore' : garmentCategory === 'lower' ? 'Inferiore' : 'Completo'}
                  </Typography>
                  {tryOnResult.metadata.processingTime && (
                    <Typography variant="caption" display="block">
                      Tempo elaborazione: {(tryOnResult.metadata.processingTime / 1000).toFixed(1)}s
                    </Typography>
                  )}
                </Box>
              )}

              <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => setActiveStep(1)}
                >
                  Prova altro prodotto
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<RestartAltIcon />}
                  onClick={resetProcess}
                >
                  Ricomincia
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}
    </Box>
  );
}

export default VirtualTryOn;